import { UNCATEGORIZED, type ProductCategoryGroups } from '@avrahami/contracts';
import type { ReactNode } from 'react';
import { categoryNodes, subtreeIds, type CategoryNode } from './category-tree';
import { toggleValue } from './lists';
import { TreeList, TreeRow } from './TreeRow';
import { isStringList, useViewState } from './view-state';
import { uiWords } from './words';

/** The key of the row that holds the products placed directly in a category with sub-categories. */
const directKey = (id: string) => `direct:${id}`;

/**
 * A product list under its categories: at first only the categories show; a category opens to
 * its sub-categories, and a sub-category opens to its products. Categories without products are
 * left out. The products of an open category are rendered by the caller (`renderProducts`), so
 * every screen keeps its own rows, and nothing is loaded for a category that was never opened.
 * The open categories are remembered with the screen: going back shows them as they were left.
 * A screen that ticks products can put a box on every row (`renderPick`) for all of its products.
 */
export function CategoryBrowser({
  groups,
  renderProducts,
  name = 'products',
  renderPick,
}: {
  groups: ProductCategoryGroups;
  /** The products placed directly in one category (`UNCATEGORIZED`: in no category). */
  renderProducts: (categoryId: string) => ReactNode;
  /** Tells two lists on one screen apart when their open categories are remembered. */
  name?: string;
  /**
   * Shown at the start of a row, for every product under it: the categories it covers (the
   * category and every category below it, the category alone for its "no sub-category" row, or
   * `UNCATEGORIZED`) and the row's name.
   */
  renderPick?: ((categoryIds: readonly string[], name: string) => ReactNode) | undefined;
}) {
  const [openKeys, setOpen] = useViewState<readonly string[]>(`open:${name}`, [], isStringList);
  const open = new Set(openKeys);
  const nodes = categoryNodes(groups.items);
  // A list without categories is just its products.
  if (nodes.length === 0) {
    return groups.uncategorized === 0 ? null : <>{renderProducts(UNCATEGORIZED)}</>;
  }

  const toggle = (key: string) => {
    setOpen((current) => toggleValue(current, key));
  };

  const renderNode = (node: CategoryNode, depth: number): ReactNode => (
    <Row
      key={node.id}
      name={node.name}
      count={node.total}
      depth={depth}
      pick={renderPick?.(subtreeIds(node), node.name)}
      open={open.has(node.id)}
      onToggle={() => {
        toggle(node.id);
      }}
    >
      {node.children.length === 0 ? (
        <Products>{renderProducts(node.id)}</Products>
      ) : (
        <ul className="flex flex-col">
          {node.children.map((child) => renderNode(child, depth + 1))}
          {node.direct === 0 ? null : (
            <Row
              name={uiWords.noSubCategory}
              count={node.direct}
              depth={depth + 1}
              pick={renderPick?.([node.id], `${node.name}: ${uiWords.noSubCategory}`)}
              open={open.has(directKey(node.id))}
              onToggle={() => {
                toggle(directKey(node.id));
              }}
            >
              <Products>{renderProducts(node.id)}</Products>
            </Row>
          )}
        </ul>
      )}
    </Row>
  );

  return (
    <TreeList label={uiWords.categories} testId="category-browser">
      {nodes.map((node) => renderNode(node, 0))}
      {groups.uncategorized === 0 ? null : (
        <Row
          name={uiWords.uncategorized}
          count={groups.uncategorized}
          depth={0}
          pick={renderPick?.([UNCATEGORIZED], uiWords.uncategorized)}
          open={open.has(UNCATEGORIZED)}
          onToggle={() => {
            toggle(UNCATEGORIZED);
          }}
        >
          <Products>{renderProducts(UNCATEGORIZED)}</Products>
        </Row>
      )}
    </TreeList>
  );
}

function Products({ children }: { children: ReactNode }) {
  return (
    <div className="bg-white p-3" data-testid="category-products">
      {children}
    </div>
  );
}

/** One category: a button that opens what is under it, with the number of products there. */
function Row({
  name,
  count,
  depth,
  pick,
  open,
  onToggle,
  children,
}: {
  name: string;
  count: number;
  depth: number;
  pick?: ReactNode;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <TreeRow
      name={name}
      depth={depth}
      open={open}
      onToggle={onToggle}
      testId="category-group"
      lead={pick}
      aside={
        <span
          data-testid="category-count"
          aria-label={`${String(count)} ${uiWords.products}`}
          className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200"
        >
          {count}
        </span>
      }
    >
      {children}
    </TreeRow>
  );
}
